import { AxiosInstance } from 'axios';
import ApiClient from './ApiClient';
import { CampaignDates } from '../types/Campaign';

// API URL from environment variables
const API_URL = process.env.REACT_APP_AI_API_URL ?? "";

// Create an instance of ApiClient for the AI suggestion service
const apiClientInstance = new ApiClient(API_URL);
const apiClient = apiClientInstance.getClient();

export class AIService {
  private apiClient: AxiosInstance;

  constructor() {
    this.apiClient = apiClient;
  }

  public async getSuggestion(
    organizationId: string,
    campaignId: string,
    fieldName: string,
    userInput?: string
  ): Promise<string> {
    try {
      console.log(`AIService - Getting suggestion for ${fieldName} with org=${organizationId}, campaign=${campaignId}`);
      console.log(`AIService - User provided input: ${userInput || 'None'}`);

      // Make API request
      // If userInput is provided, send it in the request body
      let response;
      if (userInput) {
        response = await this.apiClient.post(
          `/suggestions/${organizationId}/${campaignId}/${fieldName}`,
          { userInput }
        );
      } else {
        response = await this.apiClient.get(
          `/suggestions/${organizationId}/${campaignId}/${fieldName}`
        );
      }

      console.log(`AIService - Raw response:`, response.data);

      // Handle both JSON and plain text responses
      let suggestion: string;

      if (typeof response.data === 'object' && response.data.suggestion) {
        // If the response is JSON with a suggestion property
        suggestion = response.data.suggestion;
        console.log(`AIService - Extracted suggestion from JSON:`, suggestion);
      } else if (typeof response.data === 'string') {
        // If the response is plain text
        suggestion = response.data;
        console.log(`AIService - Using plain text response as suggestion:`, suggestion);
      } else {
        // Unexpected response format
        console.error(`AIService - Unexpected response format:`, response.data);
        throw new Error('Unexpected response format from AI service');
      }

      return suggestion;
    } catch (error) {
      console.error(`Error getting AI suggestion for ${fieldName}:`, error);

      // Return error message instead of fallback suggestions
      return "Unable to generate AI suggestion. Please try again later.";
    }
  }

  public async generateDates(
    organizationId: string,
    campaignId: string,
    kickoffDate: string,
    closingDate: string
  ): Promise<CampaignDates> {
    try {
      // Format the dates as YYYY-MM-DD for the API
      const formatDateForApi = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toISOString().split('T')[0]; // Get only the YYYY-MM-DD part
      };

      const formattedKickoff = formatDateForApi(kickoffDate);
      const formattedClosing = formatDateForApi(closingDate);

      console.log(`AIService - Generating dates with kickoff=${formattedKickoff}, closing=${formattedClosing}`);

      // Make API request with properly formatted dates
      const response = await this.apiClient.post(
        `/dates/${organizationId}/${campaignId}/generate`,
        {
          kickoff: formattedKickoff,
          closing: formattedClosing
        }
      );

      console.log(`AIService - Generated dates response:`, response.data);

      // Ensure the response has the correct field names used by our application
      // Transform response if needed to match our CampaignDates interface
      const formattedResponse: CampaignDates = {
        applications_open: response.data.applications_open || response.data.application_open || null,
        applications_close: response.data.applications_close || response.data.application_close || null,
        match_notification_date: response.data.match_notification_date || null,
        matches_finalized: response.data.matches_finalized || response.data.match_finalized || null,
        kickoff: response.data.kickoff || null,
        closing: response.data.closing || null,
        feedback_deadline: response.data.feedback_deadline || null
      };

      // Return the properly formatted dates object
      return formattedResponse;
    } catch (error) {
      console.error(`Error generating dates:`, error);

      // Return null to indicate failure
      throw new Error('Failed to generate dates from AI service');
    }
  }

  public async generateApplicationQuestions(
    organizationId: string,
    campaignId: string,
    prompt: string
  ): Promise<string[]> {
    try {
      console.log(`AIService - Generating application questions for campaign ${campaignId}`);

      // Make API request
      const response = await this.apiClient.get<string[]>(
        `/applications/${organizationId}/${campaignId}/generate`,
      );

      console.log(`AIService - Generated application questions response:`, response.data);

      // Handle the response - expecting an array of question strings
      return response.data;
    } catch (error) {
      console.error(`Error generating application questions:`, error);
      throw new Error('Failed to generate application questions');
    }
  }
}

export default AIService;
